import {
  archiveResultSchema,
  buildingListSchema,
  buildingPersonSchema,
  bookReviewSchema,
  arabicWordSchema,
  buildingSchema,
  capturePreviewSchema,
  captureResultSchema,
  createdSchema,
  healthExplanationSchema,
  inboxCountSchema,
  okSchema,
  planetListSchema,
  regionTreeSchema,
  reweightResultSchema,
  taskListSchema,
  taskNoteListSchema,
  taskNoteSchema,
  movedRegionSchema,
  taskSchema,
  universeNameSchema,
  salahHistorySchema,
  salahSettingsSchema,
  snapshotDaysSchema,
  worldSnapshotSchema,
  civilDate,
  type Building,
  strengthHistorySchema,
  type SalahEntryInput,
  type SalahMetric,
  type CapturePreview,
  type CaptureResult,
  type HealthExplanation,
  type RegionTree,
  type Task,
  type TaskImportance,
  type TaskNote,
  newsBoardSchema,
  newsStatusSchema,
  newsTasteSchema,
  newsStartedSchema,
  newsExplainSchema,
  newsBulkResultSchema,
  type NewsState,
} from '@map/shared';
import { api } from '../lib/api.js';

export const fetchUniverse = () => api.request('/universe', { schema: planetListSchema });

export const fetchTree = (planetId: string): Promise<RegionTree> =>
  api.request(`/planets/${planetId}/tree`, { schema: regionTreeSchema });

export const fetchTasks = (params: {
  regionId?: string;
  planetId?: string;
  inbox?: boolean;
  status?: 'open' | 'done' | 'dropped' | 'all';
}) => {
  const query = new URLSearchParams();
  if (params.regionId) query.set('regionId', params.regionId);
  if (params.planetId) query.set('planetId', params.planetId);
  if (params.inbox) query.set('inbox', 'true');
  if (params.status) query.set('status', params.status);
  return api.request(`/tasks?${query.toString()}`, { schema: taskListSchema });
};

/** Which days can be looked back at, and the ends of that range. */
export const fetchSnapshotDays = () =>
  api.request('/snapshots', {
    schema: snapshotDaysSchema.extend({ from: civilDate.nullable(), to: civilDate }),
  });

/** One day of the universe, read-only. A day never taken is worked out on ask. */
export const fetchSnapshot = (day: string) =>
  api.request(`/snapshots/${day}`, { schema: worldSnapshotSchema });

export const fetchInboxCount = () => api.request('/inbox/count', { schema: inboxCountSchema });

export const explainHealth = (regionId: string): Promise<HealthExplanation> =>
  api.request(`/regions/${regionId}/health`, { schema: healthExplanationSchema });

/**
 * Capture files nothing away by default: passing regionId null keeps the task
 * unplaced, so it floats in the system as rock instead of disappearing into a
 * region nobody opens.
 */
export const capture = (text: string, regionId: string | null = null): Promise<CaptureResult> =>
  api.request('/capture', { method: 'POST', body: { text, regionId }, schema: captureResultSchema });

/** The parser on its own, for the preview while typing. Creates nothing. */
export const previewCapture = (text: string): Promise<CapturePreview> =>
  api.request('/capture/preview', { method: 'POST', body: { text }, schema: capturePreviewSchema });

/** The capture form: everything the rock is made of, in one call. */
export interface TaskDraft {
  title: string;
  /** The planet it is closest to. Required of an unplaced task. */
  planetId?: string | null;
  bodyMd?: string | null;
  importance?: TaskImportance;
  urgency?: 'none' | 'soon' | 'urgent';
  targetDate?: string | null;
}

export const createTask = (draft: TaskDraft): Promise<Task> =>
  api.request('/tasks', { method: 'POST', body: { ...draft, regionId: null }, schema: taskSchema });

export const updateTask = (
  id: string,
  body: Partial<TaskDraft> & { version: number },
): Promise<Task> => api.request(`/tasks/${id}`, { method: 'PATCH', body, schema: taskSchema });

export const fetchTaskNotes = (id: string): Promise<{ notes: TaskNote[] }> =>
  api.request(`/tasks/${id}/notes`, { schema: taskNoteListSchema });

export const addTaskNote = (id: string, bodyMd: string): Promise<TaskNote> =>
  api.request(`/tasks/${id}/notes`, { method: 'POST', body: { bodyMd }, schema: taskNoteSchema });

export const setTaskStatus = (id: string, status: 'open' | 'done' | 'dropped'): Promise<Task> =>
  api.request(`/tasks/${id}/status`, { method: 'POST', body: { status }, schema: taskSchema });

export const renameUniverse = (name: string) =>
  api.request('/universe', { method: 'PATCH', body: { name }, schema: universeNameSchema });

export const updatePlanet = (
  id: string,
  body: {
    name?: string;
    artPlanetKey?: string;
    importanceWeight?: number;
    colourSeed?: number;
    archived?: boolean;
  },
) => api.request(`/planets/${id}`, { method: 'PATCH', body, schema: okSchema });

export const createPlanet = (name: string, artPlanetKey: string, importanceWeight?: number) =>
  api.request('/planets', {
    method: 'POST',
    body: { name, artPlanetKey, importanceWeight },
    schema: createdSchema,
  });

export const createRegion = (body: {
  planetId: string;
  parentId?: string | null;
  kind: 'continent' | 'country' | 'city' | 'district';
  name: string;
  importanceWeight?: number;
}) => api.request('/regions', { method: 'POST', body, schema: createdSchema });

export const renameRegion = (id: string, name: string, version: number) =>
  api.request(`/regions/${id}`, { method: 'PATCH', body: { name, version }, schema: createdSchema });

export const updateRegion = (
  id: string,
  body: { version: number; name?: string; geometrySeed?: number; geometryScale?: number },
) => api.request(`/regions/${id}`, { method: 'PATCH', body, schema: createdSchema });

export const reweight = (weights: Array<{ id: string; weight: number }>) =>
  api.request('/regions/reweight', { method: 'POST', body: { weights }, schema: reweightResultSchema });

export const archiveRegion = (id: string, archived: boolean) =>
  api.request(`/regions/${id}/archive`, {
    method: 'POST',
    body: { archived },
    schema: archiveResultSchema,
  });

export const moveRegion = (id: string, parentId: string | null) =>
  api.request(`/regions/${id}/move`, { method: 'POST', body: { parentId }, schema: movedRegionSchema });

export const markReviewed = (id: string, note?: string) =>
  api.request(`/regions/${id}/review`, { method: 'POST', body: { note }, schema: okSchema });

export const fetchBuildings = (regionId: string) =>
  api.request(`/regions/${regionId}/buildings`, { schema: buildingListSchema });

/** What Ilm knows about a person on the map (Faith › Seeking Knowledge › People). */
export const fetchPerson = (buildingId: string) =>
  api.request(`/buildings/${buildingId}/person`, { schema: buildingPersonSchema });

export const fetchBookReview = (buildingId: string) =>
  api.request(`/buildings/${buildingId}/review`, { schema: bookReviewSchema });

export const fetchArabicWord = (buildingId: string, wordId: number) =>
  api.request(`/buildings/${buildingId}/words/${wordId}`, { schema: arabicWordSchema });

export const fetchPlanetBuildings = (planetId: string) =>
  api.request(`/planets/${planetId}/buildings`, { schema: buildingListSchema });

export const createBuilding = (body: {
  regionId: string;
  name: string;
  unit?: string;
  floorsTotal: number;
  rhythmDays?: number | null;
}): Promise<Building> => api.request('/buildings', { method: 'POST', body, schema: buildingSchema });

export const addFloors = (id: string, floors = 1): Promise<Building> =>
  api.request(`/buildings/${id}/floors`, { method: 'POST', body: { floors }, schema: buildingSchema });

export const fetchStrength = (buildingId: string) =>
  api.request(`/buildings/${buildingId}/strength`, { schema: strengthHistorySchema });

export const setStrengthReset = (buildingId: string, day: string, reset: boolean) =>
  api.request(`/buildings/${buildingId}/strength/resets/${day}`, { method: reset ? 'PUT' : 'DELETE', schema: okSchema });

export const setStrengthBegan = (buildingId: string, day: string) =>
  api.request(`/buildings/${buildingId}/strength/began`, { method: 'PUT', body: { day }, schema: okSchema });

export const fetchSalahHistory = (buildingId: string) =>
  api.request(`/buildings/${buildingId}/salah`, { schema: salahHistorySchema });

export const saveSalahEntry = (buildingId: string, day: string, entry: SalahEntryInput) =>
  api.request(`/buildings/${buildingId}/salah/${day}`, { method: 'PUT', body: entry, schema: okSchema });

export const deleteSalahEntry = (buildingId: string, day: string) =>
  api.request(`/buildings/${buildingId}/salah/${day}`, { method: 'DELETE', schema: okSchema });

export const fetchSalahSettings = () => api.request('/salah/settings', { schema: salahSettingsSchema });

export const saveSalahSettings = (order: SalahMetric[]) =>
  api.request('/salah/settings', { method: 'PUT', body: { order }, schema: salahSettingsSchema });

// ---- the Newswire satellite ------------------------------------------------

export const fetchNewsBoard = () => api.request('/news', { schema: newsBoardSchema });
export const fetchNewsStatus = () => api.request('/news/status', { schema: newsStatusSchema });
export const refreshNews = () => api.request('/news/refresh', { method: 'POST', schema: newsStartedSchema });
export const triageNews = (itemId: string, action: NewsState) =>
  api.request(`/news/items/${itemId}/state`, { method: 'PUT', body: { action }, schema: okSchema });
/** Many stories at once; `'all'` passes on every undecided story, shown or not. */
export const bulkNews = (ids: string[] | 'all', action: 'dismissed' | 'fresh') =>
  api.request('/news/bulk', {
    method: 'POST',
    body: ids === 'all' ? { all: true, action } : { ids, action },
    schema: newsBulkResultSchema,
  });
export const markNewsSeen = (ids: string[]) =>
  api.request('/news/seen', { method: 'POST', body: { ids }, schema: okSchema });
export const markThreadSeen = (threadId: string) =>
  api.request(`/news/threads/${threadId}/seen`, { method: 'POST', schema: okSchema });
export const checkThreadNow = (threadId: string) =>
  api.request(`/news/threads/${threadId}/check`, { method: 'POST', schema: newsStartedSchema });
export const setThreadTerms = (threadId: string, terms: string[]) =>
  api.request(`/news/threads/${threadId}/terms`, { method: 'PUT', body: { terms }, schema: okSchema });
export const explainNews = (itemId: string) =>
  api.request(`/news/items/${itemId}/explain`, { method: 'POST', schema: newsExplainSchema });
export const unfollowThread = (threadId: string) =>
  api.request(`/news/threads/${threadId}`, { method: 'DELETE', schema: okSchema });
export const mergeThreads = (threadId: string, into: string) =>
  api.request(`/news/threads/${threadId}/merge`, { method: 'POST', body: { into }, schema: okSchema });
export const splitThread = (threadId: string) =>
  api.request(`/news/threads/${threadId}/split`, { method: 'POST', schema: okSchema });
export const setGroupLabel = (threadId: string, label: string) =>
  api.request(`/news/threads/${threadId}/label`, { method: 'PUT', body: { label }, schema: okSchema });
export const followInto = (itemId: string, threadId: string) =>
  api.request(`/news/items/${itemId}/follow-into`, { method: 'POST', body: { threadId }, schema: okSchema });
export const fetchNewsTaste = () => api.request('/news/taste', { schema: newsTasteSchema });
export const saveNewsRules = (rules: { id?: string; text: string; polarity: 'more' | 'less'; source?: 'learned' | 'user' }[]) =>
  api.request('/news/taste/rules', { method: 'PUT', body: { rules }, schema: okSchema });
export const resetNewsTaste = () => api.request('/news/taste', { method: 'DELETE', schema: okSchema });
